// src/sim/world.ts — SimWorld: sabit kapasiteli fighters[], items[] havuzu, arena + query, olay havuzu, systems ENJEKSİYONU,
// step (ARCHITECTURE §3 10 adım), snapshotPrev, hash (girdiler hariç). moves/grab/hits/items dosyalarını import ETMEZ;
// yalnız this.systems.* çağırır (WP9 gerçek modülleri bağlar). three'siz; sıcak yolda tahsis yok.
import { FState, inputFrame } from '../core/types.ts';
import type {
  ArenaDef, ClipDataSet, EventList, Fighter, InputBuffer, InputFrame, Item, MatchState, MoveDef, SimSystems, SimWorld,
} from '../core/types.ts';
import { createEventList } from '../core/events.ts';
import { MAX_FIGHTERS, MAX_ITEMS, NO_ID, NO_TICK } from '../config/constants.ts';
import { ARENA } from '../config/arena.ts';
import { MOVES } from '../config/moves.ts';
import { CHARACTERS } from '../config/characters.ts';
import { createArenaQuery } from './arena.ts';
import type { ArenaQueryEx } from './arena.ts';
import { createFighter, createInputBuffer, stepInputBuffer, stepLocomotion, stepTimers } from './fighter.ts';
import { belowKillPlane, computePush } from './collision.ts';
import { beginCountdown, createMatchState, fell, stepMatch, stepMatchTimers } from './match.ts';

export interface FighterSpec { charIdx: number; isBot: boolean; weightMult?: number }
export interface WorldOptions {
  seed: number;
  fighters: readonly FighterSpec[];
  systems: SimSystems;
  clips: ClipDataSet;
  arena?: ArenaDef;
  moves?: readonly MoveDef[];
  /** false → geri sayımsız, herkes Idle ve girdi açık (testler). Varsayılan true (DESIGN §13 giriş). */
  countdown?: boolean;
}

/** Boş eşya (havuz; WP4 'dead' girişleri yeniden kullanır). */
export function blankItem(id: number): Item {
  return {
    id, kind: '1h', propId: 'sword_1handed', state: 'dead',
    pos: { x: 0, y: 0, z: 0 }, vel: { x: 0, y: 0, z: 0 }, yaw: 0, spin: 0,
    holder: NO_ID, thrower: NO_ID, spawnedAt: NO_TICK, pickableAt: NO_TICK, despawnAt: NO_TICK,
    hits: 0, breakAt: NO_TICK, hitMask: 0, length: 0, holderOffsetY: 0,
  };
}

/** Kilitli girdi (geri sayım / Falling / maç sonu): nötr kare — dövüşçünün kendi InputFrame'i değiştirilmez. */
const NEUTRAL: InputFrame = inputFrame();

// ── hash: float bitleri üzerinden 32-bit karıştırma (tahsissiz) ──
const F64 = new Float64Array(1);
const U32 = new Uint32Array(F64.buffer);
function mix(h: number, v: number): number {
  h = Math.imul(h ^ v, 0x9e3779b1);
  return (h ^ (h >>> 15)) >>> 0;
}
function hashNum(h: number, v: number): number {
  F64[0] = v;
  return mix(mix(h, U32[0]!), U32[1]!);
}
function hashValue(h: number, v: unknown): number {
  switch (typeof v) {
    case 'number': return hashNum(h, v);
    case 'boolean': return mix(h, v ? 1 : 2);
    case 'string': { for (let i = 0; i < v.length; i++) h = mix(h, v.charCodeAt(i)); return mix(h, 0x5f); }
    case 'object': {
      if (v === null) return mix(h, 7);
      if (v instanceof Int32Array) { for (let i = 0; i < v.length; i++) h = mix(h, v[i]! >>> 0); return h; }
      const o = v as { x?: number; y?: number; z?: number };
      if (typeof o.x === 'number' && typeof o.y === 'number' && typeof o.z === 'number') return hashNum(hashNum(hashNum(h, o.x), o.y), o.z);
      return h;
    }
    default: return h;
  }
}
/** Dövüşçü / eşya alan adları (kurulumda bir kez; hash sıcak yolda tahsissiz döner). */
const FIGHTER_KEYS: readonly string[] = Object.keys(createFighter(0, 0, false, 1, 0)).filter((k) => k !== 'prevPos' && k !== 'prevYaw');
const ITEM_KEYS: readonly string[] = Object.keys(blankItem(0));

export class World implements SimWorld {
  tick = 0;
  readonly seed: number;
  readonly fighters: Fighter[] = [];
  readonly items: Item[] = [];
  readonly arena: ArenaDef;
  readonly query: ArenaQueryEx;
  readonly match: MatchState = createMatchState();
  readonly events: EventList = createEventList();
  readonly moves: readonly MoveDef[];
  readonly clips: ClipDataSet;
  systems: SimSystems;
  readonly inputs: InputFrame[] = [];
  readonly buffers: InputBuffer[] = [];
  /** stepTimers sonucu: bu tick hitstop'ta olanlar 4–6'yı atlar. (İtişme yer değiştirmeleri collision.PUSH_X/PUSH_Z modül dizilerinde.) */
  private readonly frozen = new Uint8Array(MAX_FIGHTERS);

  constructor(o: WorldOptions) {
    if (o.fighters.length < 1 || o.fighters.length > MAX_FIGHTERS) throw new Error('fighters 1..' + MAX_FIGHTERS);
    this.seed = o.seed | 0;
    this.arena = o.arena ?? ARENA;
    this.query = createArenaQuery(this.arena);
    this.moves = o.moves ?? MOVES;
    this.clips = o.clips;
    this.systems = o.systems;
    for (let i = 0; i < o.fighters.length; i++) {
      const s = o.fighters[i]!;
      const w = s.weightMult ?? CHARACTERS[s.charIdx]?.weightMult ?? 1;
      this.fighters.push(createFighter(i, s.charIdx, s.isBot, w, (this.seed ^ (i * 0x9e3779b1)) | 0));
      this.inputs.push(inputFrame());
      this.buffers.push(createInputBuffer());
    }
    for (let i = 0; i < MAX_ITEMS; i++) this.items.push(blankItem(i));
    beginCountdown(this);
    if (o.countdown === false) {
      this.match.phase = 'fight'; this.match.phaseUntil = 0; this.match.countdown = 0;
      for (let i = 0; i < this.fighters.length; i++) { const f = this.fighters[i]!; f.state = FState.Idle; f.stateTick = 0; f.stateUntil = NO_TICK; f.controlLockUntil = NO_TICK; }
    }
  }

  /** Dövüşçünün bu tick'te gördüğü girdi: kontrol kilidindeyse nötr. */
  private inputFor(f: Fighter): InputFrame {
    return this.tick < f.controlLockUntil ? NEUTRAL : this.inputs[f.id]!;
  }

  step(): void {
    const fs = this.fighters;
    const n = fs.length;
    const sys = this.systems;
    const tick = this.tick;
    // 1. maç zamanlayıcısı; globalHitstop > 0 → 2–8 atlanır
    const skip = stepMatchTimers(this);
    // Ring-out donmasında (globalHitstop) tick akar ama hiçbir durum ilerlemez ve animMap TÜM dövüşçüleri `paused` çizer →
    // donan tick'ler sayılmazsa çözülme anında herkesin klip zamanı sıçrar (entegrasyon 3. tur; anim devri).
    if (skip) for (let i = 0; i < n; i++) fs[i]!.frozenTicks++;
    if (!skip) {
      // 2. zamanlayıcılar + knockdown FSM
      for (let i = 0; i < n; i++) { const f = fs[i]!; this.frozen[i] = stepTimers(this, f) ? 1 : 0; if (!this.frozen[i]) sys.stepKnockdown(this, f); }
      // 3. girdi tamponu (hitstop'ta da yazılır; yaşlanma adım 2'de telafi edilir)
      for (let i = 0; i < n; i++) { const f = fs[i]!; stepInputBuffer(this, f, this.inputFor(f)); }
      // 4. hamle FSM (dodge/blok/taunt girdileri içinde: systems.tryDodge / tryBlock)
      for (let i = 0; i < n; i++) { if (!this.frozen[i]) sys.stepMoves(this, fs[i]!); }
      // 5. tutma FSM
      sys.stepGrab(this);
      // 6. hareket + çarpışma: push, sonra dövüşçü başına dx = vel·DT + rmDx + push; kenar freni; zemin; kill plane
      computePush(this);
      for (let i = 0; i < n; i++) {
        const f = fs[i]!;
        if (this.frozen[i]) continue;
        stepLocomotion(this, f, this.inputFor(f));
      }
      for (let i = 0; i < n; i++) { const f = fs[i]!; if (belowKillPlane(this, f)) fell(this, f); }
      // 7. vuruş çözümü (bu tick'in sonuç konumlarıyla)
      sys.resolveHits(this);
      // 8. eşyalar
      sys.items.step(this, tick);
    }
    // 9. maç: yeniden doğuş, eleme, kazanan
    stepMatch(this);
    // 10.
    this.tick++;
  }

  snapshotPrev(): void {
    const fs = this.fighters;
    for (let i = 0; i < fs.length; i++) {
      const f = fs[i]!;
      f.prevPos.x = f.pos.x; f.prevPos.y = f.pos.y; f.prevPos.z = f.pos.z; f.prevYaw = f.yaw;
    }
  }

  /** Determinizm hash'i: seed + tick + maç + girdi tamponları + dövüşçüler (prev* hariç) + eşyalar.
   *  O TICK'İN girdileri hash'lenmez (dünya durumu değil, dışarıdan gelen parametre) — ama `buffers` hash'lenir:
   *  tamponlanmış basışlar tick'ler arası TAŞINAN durumdur ve geleceği belirler (`bufferedPress`/`consumePress`,
   *  hitstop'ta yaşlanmaz). NEDEN önemli: ağ sapma dedektörü olarak kullanılıyor; hash eşit ama tampon farklı olan
   *  iki dünyanın yarısı 60 tick içinde ayrışıyor (proposals/multiplayer.md §4.2 ölçümü). `seed` de karışır:
   *  yalnız `hashNoise` üzerinden duruma sızdığı için farkı tick'ler sonra görünür, hash'te ise anında. */
  hash(): number {
    let h = 0x811c9dc5;
    h = mix(h, this.seed >>> 0);
    h = mix(h, this.tick);
    for (let i = 0; i < this.buffers.length; i++) {
      const b = this.buffers[i]!;
      h = hashValue(hashValue(h, b.pressTick), b.releaseTick);
    }
    const m = this.match;
    h = mix(h, m.phase === 'countdown' ? 1 : m.phase === 'fight' ? 2 : 3);
    h = mix(mix(mix(mix(h, m.phaseUntil >>> 0), m.countdown), m.winner >>> 0), m.globalHitstop);
    for (let i = 0; i < this.fighters.length; i++) {
      const f = this.fighters[i]! as unknown as Record<string, unknown>;
      for (let k = 0; k < FIGHTER_KEYS.length; k++) h = hashValue(h, f[FIGHTER_KEYS[k]!]);
    }
    for (let i = 0; i < this.items.length; i++) {
      const it = this.items[i]! as unknown as Record<string, unknown>;
      for (let k = 0; k < ITEM_KEYS.length; k++) h = hashValue(h, it[ITEM_KEYS[k]!]);
    }
    return h >>> 0;
  }
}

export const createWorld = (o: WorldOptions): World => new World(o);
